import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ClipboardCheck, Heart } from "lucide-react";

const CONDITIONS = [
  "Beau's Lines", "Chloronychia/Pseudomonas", "Corrugated/Ridged Nails",
  "Diabetes", "Eczema/Dermatitis", "Habit Tic", "Hangnail", "Hematoma",
  "Koilonychia", "Leukonychia", "Median Nail Dystrophy", "Onychomadesis",
  "Onycholysis", "Psoriasis", "Paronychia", "Splinter Haemorrhage",
  "Yeast Infection", "Fungal Infections (Warts/Ringworm/Athlete's Foot)"
];

export default function ConsultationForm({ clientId, clientName, clientEmail, onComplete, compact = false }) {
  const [salonName, setSalonName] = useState('');
  useEffect(() => {
    axios.get(`${API}/settings?lite=true`).then(r => setSalonName(r.data?.salon_name || '')).catch(() => {});
  }, []);
  const [form, setForm] = useState({
    name: clientName || '',
    dob: '',
    email: clientEmail || '',
    contact_number: '',
    emergency_contact: '',
    allergies: '',
    medical_history: '',
    current_medication: '',
    conditions: [],
    occupation_hobbies: '',
    nail_aspirations: '',
    confirmed: false,
    technician_name: '',
    date_signed: new Date().toISOString().split('T')[0]
  });
  const [submitting, setSubmitting] = useState(false);

  const toggleCondition = (c) => {
    setForm(prev => ({
      ...prev,
      conditions: prev.conditions.includes(c) 
        ? prev.conditions.filter(x => x !== c) 
        : [...prev.conditions, c]
    }));
  };

  const u = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!form.name) {
      toast.error("Please enter your name");
      return;
    }
    if (!form.confirmed) {
      toast.error("Please confirm the information is correct");
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/consultation-forms`, {
        ...form,
        client_id: clientId || null,
        client_name: form.name,
        client_email: form.email
      });
      toast.success("Consultation form submitted successfully!");
      if (onComplete) onComplete(res.data);
    } catch (e) {
      toast.error("Failed to submit form");
    }
    setSubmitting(false);
  };

  return (
    <div className={compact ? "space-y-4" : "space-y-6"}>
      {/* Header */}
      {!compact && (
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-2">
            <ClipboardCheck size={28} className="text-primary" />
            <h2 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>
              {salonName ? `${salonName} Consultation Form` : 'Consultation Form'}
            </h2>
          </div>
          <p className="text-sm text-muted-foreground">Please complete this form before your first appointment</p>
        </div>
      )}

      {/* Personal Details */}
      <Card className={`${compact ? 'p-4' : 'p-6'} rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)]`}>
        <h3 className="text-sm font-semibold mb-3 text-primary uppercase tracking-wider">Personal Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Name *</Label>
            <Input value={form.name} onChange={e => u('name', e.target.value)} className="mt-1 rounded-xl" placeholder="Full name" data-testid="consultation-name" />
          </div>
          <div>
            <Label className="text-xs">Date of Birth (for insurance purposes)</Label>
            <Input type="date" value={form.dob} onChange={e => u('dob', e.target.value)} className="mt-1 rounded-xl" />
          </div>
          <div>
            <Label className="text-xs">Email Address (for correspondence)</Label>
            <Input type="email" value={form.email} onChange={e => u('email', e.target.value)} className="mt-1 rounded-xl" placeholder="your@email.com" />
          </div>
          <div>
            <Label className="text-xs">Contact Number (for correspondence)</Label>
            <Input value={form.contact_number} onChange={e => u('contact_number', e.target.value)} className="mt-1 rounded-xl" placeholder="07xxx xxx xxx" />
          </div>
          <div>
            <Label className="text-xs">Emergency Contact (for emergencies)</Label>
            <Input value={form.emergency_contact} onChange={e => u('emergency_contact', e.target.value)} className="mt-1 rounded-xl" placeholder="Name & phone number" />
          </div>
          <div>
            <Label className="text-xs">Any allergies?</Label>
            <Textarea value={form.allergies} onChange={e => u('allergies', e.target.value)} className="mt-1 rounded-xl" rows={2} placeholder="List any known allergies" />
          </div>
        </div>
      </Card>

      {/* Medical Info */}
      <Card className={`${compact ? 'p-4' : 'p-6'} rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)]`}>
        <h3 className="text-sm font-semibold mb-3 text-primary uppercase tracking-wider">Medical Information</h3>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Medical History</Label>
            <Textarea value={form.medical_history} onChange={e => u('medical_history', e.target.value)} className="mt-1 rounded-xl" rows={2} placeholder="Any relevant medical history" />
          </div>
          <div>
            <Label className="text-xs">Current Medication (please list all medication even if it seems irrelevant)</Label>
            <Textarea value={form.current_medication} onChange={e => u('current_medication', e.target.value)} className="mt-1 rounded-xl" rows={2} placeholder="List all current medications" />
          </div>
        </div>
      </Card>

      {/* Conditions */}
      <Card className={`${compact ? 'p-4' : 'p-6'} rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)]`}>
        <h3 className="text-sm font-semibold mb-1 text-primary uppercase tracking-wider">Nail & Skin Conditions</h3>
        <p className="text-xs text-muted-foreground mb-3 italic">Do you suffer from any of the following? (Tick all that apply)</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {CONDITIONS.map(c => (
            <label key={c} className="flex items-center gap-2 text-xs cursor-pointer p-1.5 rounded-lg hover:bg-secondary transition-colors">
              <Checkbox 
                checked={form.conditions.includes(c)} 
                onCheckedChange={() => toggleCondition(c)} 
              />
              <span>{c}</span>
            </label>
          ))}
        </div>
      </Card>

      {/* Lifestyle */}
      <Card className={`${compact ? 'p-4' : 'p-6'} rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)]`}>
        <h3 className="text-sm font-semibold mb-3 text-primary uppercase tracking-wider">Occupation / Hobbies / Lifestyle</h3>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Your occupation, hobbies, and lifestyle</Label>
            <Textarea value={form.occupation_hobbies} onChange={e => u('occupation_hobbies', e.target.value)} className="mt-1 rounded-xl" rows={2} placeholder="e.g. Nurse, gym 3x week, avid gardener" />
          </div>
          <div>
            <Label className="text-xs">What aspirations do you have from your nails? (short term and long term goals)</Label>
            <Textarea value={form.nail_aspirations} onChange={e => u('nail_aspirations', e.target.value)} className="mt-1 rounded-xl" rows={3} placeholder="e.g. Stronger natural nails, occasional gel sets for events, grow my nails longer" />
          </div>
        </div>
      </Card>

      {/* Confirmation */}
      <Card className={`${compact ? 'p-4' : 'p-6'} rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)]`}>
        <div className="space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox 
              checked={form.confirmed} 
              onCheckedChange={v => u('confirmed', v)} 
              data-testid="consultation-confirm"
            />
            <span className="text-sm font-medium">I confirm that all the information provided is correct</span>
          </label>
        </div>
      </Card>

      <Button 
        onClick={handleSubmit} 
        disabled={submitting || !form.confirmed} 
        className="w-full rounded-xl h-12 text-base"
        data-testid="consultation-submit"
      >
        {submitting ? 'Submitting...' : 'Submit Consultation Form'}
        <Heart size={16} className="ml-2" />
      </Button>
    </div>
  );
}
